import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import Button from '../common/Button';
import { Modal, ModalContent, ModalCloseButton } from '../common';
import { ManagementActionRow } from '../clubs/ManagementHubSection';
import SessionTemplateForm from './SessionTemplateForm';

const SessionTemplatesSection = ({ selectedClub, currentUser, embeddedInHub = false }) => {
  const [sessionSetups, setSessionSetups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingSetup, setEditingSetup] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (selectedClub?.id) {
      fetchSessionSetups();
    }
  }, [selectedClub?.id]);

  const fetchSessionSetups = async () => {
    if (!selectedClub?.id) return;

    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('club_session_setup')
        .select(`
          *,
          session_types!inner(name, description)
        `)
        .eq('club_id', selectedClub.id)
        .order('day_of_week, start_time');

      if (fetchError) throw fetchError;
      setSessionSetups(data || []);
    } catch (err) {
      console.error('Error fetching session setups:', err);
      setError('Failed to load session setups');
    }
  };

  const handleTemplateSubmit = async (templateData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingSetup) {
        const { data, error: updateError } = await supabase
          .from('club_session_setup')
          .update(templateData)
          .eq('id', editingSetup.id)
          .select();

        if (updateError) throw updateError;

        if (!data || data.length === 0) {
          throw new Error(
            'No rows were updated. The template may not exist or you may not have permission to update it.'
          );
        }

        setSuccess('Session template updated successfully!');
      } else {
        const { error: insertError } = await supabase
          .from('club_session_setup')
          .insert(templateData)
          .select()
          .single();

        if (insertError) throw insertError;
        setSuccess('Session template created successfully!');
      }

      await fetchSessionSetups();
      setEditingSetup(null);
      setShowModal(false);
    } catch (err) {
      console.error('Error saving session template:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setup) => {
    setEditingSetup(setup);
    setShowModal(true);
  };

  const handleCancelEdit = () => {
    setEditingSetup(null);
    setShowModal(false);
  };

  const handleDelete = async (setupId) => {
    if (!window.confirm('Are you sure you want to delete this session template?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('club_session_setup')
        .delete()
        .eq('id', setupId);

      if (deleteError) throw deleteError;

      setSuccess('Session template deleted successfully!');
      await fetchSessionSetups();
    } catch (err) {
      console.error('Error deleting session template:', err);
      setError('Failed to delete session template');
    }
  };

  const getDayLabel = (dayOfWeek) => {
    const daysOfWeek = [
      { value: '0', label: 'Sunday' },
      { value: '1', label: 'Monday' },
      { value: '2', label: 'Tuesday' },
      { value: '3', label: 'Wednesday' },
      { value: '4', label: 'Thursday' },
      { value: '5', label: 'Friday' },
      { value: '6', label: 'Saturday' },
    ];
    return daysOfWeek.find((day) => day.value === dayOfWeek.toString())?.label || 'Unknown';
  };

  const formatTime = (time) => (time ? String(time).slice(0, 5) : null);

  const getTemplateDescription = (setup) => {
    const parts = [
      setup.session_types?.name,
      getDayLabel(setup.day_of_week),
      formatTime(setup.start_time) || '—',
      setup.end_time ? `until ${formatTime(setup.end_time)}` : null,
      setup.is_active ? 'Active' : 'Inactive',
    ].filter(Boolean);
    return parts.join(' · ');
  };

  const renderTemplateActions = (setup) => (
    <>
      <Button onClick={() => handleEdit(setup)} variant="outline" size="small">
        Edit
      </Button>
      <Button
        onClick={() => handleDelete(setup.id)}
        variant="outline"
        size="small"
        className="border-danger text-danger hover:bg-danger-light"
      >
        Delete
      </Button>
    </>
  );

  if (!selectedClub) {
    return null;
  }

  const feedbackMessages = (
    <>
      {success ? (
        <Card variant="success" size="small">
          <Typography variant="body" className="text-text-primary">
            {success}
          </Typography>
        </Card>
      ) : null}
      {error ? (
        <Card variant="danger" size="small">
          <Typography variant="body" className="text-text-primary">
            {error}
          </Typography>
        </Card>
      ) : null}
    </>
  );

  const templateModal = (
    <Modal isOpen={showModal} onClose={handleCancelEdit}>
      <ModalContent
        size="large"
        padding={false}
        scrollBody={false}
        className="flex min-h-0 flex-col overflow-hidden"
      >
        <div className="shrink-0 border-b border-border-medium p-4 sm:p-6">
          <div className="flex items-start justify-between gap-2 pr-8">
            <Heading level={2}>
              {editingSetup ? 'Edit session template' : 'Create session template'}
            </Heading>
            <ModalCloseButton onClose={handleCancelEdit} />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <SessionTemplateForm
            onSubmit={handleTemplateSubmit}
            onCancel={handleCancelEdit}
            initialData={editingSetup}
            loading={loading}
            error={error}
            clubId={selectedClub?.id}
            isModal={true}
          />
        </div>
      </ModalContent>
    </Modal>
  );

  if (embeddedInHub) {
    return (
      <div className="space-y-2">
        {feedbackMessages}
        {sessionSetups.map((setup) => (
          <ManagementActionRow
            key={setup.id}
            iconType="planning"
            title={setup.session_name}
            description={getTemplateDescription(setup)}
            onClick={() => handleEdit(setup)}
            actions={renderTemplateActions(setup)}
          />
        ))}
        <ManagementActionRow
          iconType="planning"
          title={sessionSetups.length === 0 ? 'Create template' : 'Add template'}
          onClick={() => setShowModal(true)}
        />
        {templateModal}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card variant="default" size="medium">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Heading level={3}>Quick setup templates</Heading>
          <Button
            onClick={() => setShowModal(true)}
            variant="primary"
            size="medium"
            className="w-full sm:w-auto shrink-0"
          >
            Create template
          </Button>
        </div>
      </Card>

      {feedbackMessages}
      {templateModal}

      <Card variant="default" size="medium">
        {sessionSetups.length === 0 ? (
          <Text className="text-sm text-text-secondary py-4">
            No quick setup templates yet. Create your first template to streamline session
            creation.
          </Text>
        ) : (
          <div className="space-y-2">
            {sessionSetups.map((setup) => (
              <ManagementActionRow
                key={setup.id}
                iconType="planning"
                title={setup.session_name}
                description={getTemplateDescription(setup)}
                onClick={() => handleEdit(setup)}
                actions={renderTemplateActions(setup)}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SessionTemplatesSection;
